/**
 * OnboardingPage — first screen the user sees when opening the Mini App.
 *
 * Flow
 * ─────
 *  1. Mount: init Telegram WebApp, fetch the current user via `users.getMe()`.
 *  2. If `getMe` 401s → render an "open this from Telegram" splash. The user
 *     cannot recover client-side, so we deliberately do not show a retry.
 *  3. If `tonAddress` is already linked on the profile → flash a brief
 *     "welcome back" message and auto-navigate to /deals.
 *  4. Otherwise → render the marketing hero + three feature bullets +
 *     `<TonConnectButton />`. When `useTonAddress()` flips from empty to
 *     non-empty we POST it to `/users/wallet` and forward to /deals.
 *  5. A tiny "skip for now" link lets the user defer wallet linking; they
 *     can connect later from the profile page.
 *
 * The page intentionally hides the Telegram MainButton — TonConnect's own
 * button is the primary CTA — and the BackButton is hidden globally on the
 * root route via `AppShell`.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTonAddress, useTonConnectUI } from '@tonconnect/ui-react';
import { Wallet } from 'lucide-react';
import { Scale, Shield, Zap, type LucideIcon } from 'lucide-react';

import { AppShell } from '@/components/layout/AppShell';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { isApiError, users } from '@/lib/api';
import type { UserProfile } from '@/lib/api/types';
import { useT } from '@/lib/i18n';
import { useAppStore, type UserSnapshot } from '@/lib/state/store';
import { haptics } from '@/lib/telegram/haptics';
import { initTelegramWebApp } from '@/lib/telegram/webapp';
import { ROUTES } from '@/routes/router';

type LoadState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready'; user: UserProfile }
  | { kind: 'unauthorized' }
  | { kind: 'error'; message: string };

interface FeatureSpec {
  icon: LucideIcon;
  titleKey:
    | 'onboarding.feature.nonCustodial.title'
    | 'onboarding.feature.instant.title'
    | 'onboarding.feature.fair.title';
  bodyKey:
    | 'onboarding.feature.nonCustodial.body'
    | 'onboarding.feature.instant.body'
    | 'onboarding.feature.fair.body';
}

const FEATURES: readonly FeatureSpec[] = [
  {
    icon: Shield,
    titleKey: 'onboarding.feature.nonCustodial.title',
    bodyKey: 'onboarding.feature.nonCustodial.body',
  },
  {
    icon: Zap,
    titleKey: 'onboarding.feature.instant.title',
    bodyKey: 'onboarding.feature.instant.body',
  },
  {
    icon: Scale,
    titleKey: 'onboarding.feature.fair.title',
    bodyKey: 'onboarding.feature.fair.body',
  },
] as const;

/** Map a server `UserProfile` to the lean `UserSnapshot` shape kept in Zustand. */
function toSnapshot(user: UserProfile): UserSnapshot {
  return {
    id: user.id,
    telegramId: user.telegramId,
    username: user.username,
    tonAddress: user.tonAddress,
    reputationScore: user.reputationScore,
    role: user.role,
    isBanned: user.isBanned,
  };
}

export function OnboardingPage(): JSX.Element {
  const t = useT();
  const navigate = useNavigate();
  const setUser = useAppStore((state) => state.setUser);

  const [state, setState] = useState<LoadState>({ kind: 'idle' });
  const [welcomeName, setWelcomeName] = useState<string | null>(null);
  const [linkingWallet, setLinkingWallet] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);

  const tonAddress = useTonAddress();
  const [tonConnectUI] = useTonConnectUI();

  // Track the previous TonConnect address so we only POST to /users/wallet
  // on the empty-to-filled transition. Without this, every re-render that
  // observes a non-empty address would re-trigger the network call.
  const previousAddress = useRef<string>('');

  /**
   * Initial profile fetch. Runs once on mount and re-runs from the error
   * card's "try again" button.
   */
  const loadProfile = useCallback(async (): Promise<void> => {
    setState({ kind: 'loading' });
    try {
      const { user } = await users.getMe();
      setUser(toSnapshot(user));

      if (user.tonAddress) {
        // Wallet already linked → quick welcome flash, then redirect.
        const greetingName = user.firstName ?? user.username ?? '';
        setWelcomeName(greetingName);
        haptics.success();
        setState({ kind: 'ready', user });
        window.setTimeout(() => {
          navigate(ROUTES.deals, { replace: true });
        }, 300);
        return;
      }

      setState({ kind: 'ready', user });
    } catch (err) {
      if (isApiError(err) && err.status === 401) {
        setState({ kind: 'unauthorized' });
        return;
      }
      const message =
        err instanceof Error ? err.message : t('common.error');
      setState({ kind: 'error', message });
    }
  }, [navigate, setUser, t]);

  // Bootstrap: init Telegram WebApp once, then fetch the profile.
  useEffect(() => {
    initTelegramWebApp();
    void loadProfile();
  }, [loadProfile]);

  // Wallet-linked side effect: when TonConnect returns a fresh address and
  // the backend profile does not yet have one, persist it and navigate.
  useEffect(() => {
    if (state.kind !== 'ready') return;
    if (state.user.tonAddress) return;
    if (!tonAddress) {
      previousAddress.current = '';
      return;
    }
    if (previousAddress.current === tonAddress) return;
    previousAddress.current = tonAddress;

    let cancelled = false;
    const link = async (): Promise<void> => {
      setLinkingWallet(true);
      setLinkError(null);
      try {
        const { user } = await users.updateWallet({ tonAddress });
        if (cancelled) return;
        setUser(toSnapshot(user));
        haptics.success();
        navigate(ROUTES.deals, { replace: true });
      } catch (err) {
        if (cancelled) return;
        haptics.error();
        const message =
          err instanceof Error ? err.message : t('common.error');
        setLinkError(message);
        // Disconnect so the user can retry from a clean slate.
        try {
          await tonConnectUI.disconnect();
        } catch {
          // Ignore disconnect failures - the wallet may already be gone.
        }
        previousAddress.current = '';
      } finally {
        if (!cancelled) setLinkingWallet(false);
      }
    };

    void link();
    return () => {
      cancelled = true;
    };
  }, [navigate, setUser, state, t, tonAddress, tonConnectUI]);

  const handleSkip = useCallback((): void => {
    haptics.light();
    navigate(ROUTES.deals, { replace: true });
  }, [navigate]);

  const handleRetry = useCallback((): void => {
    haptics.light();
    void loadProfile();
  }, [loadProfile]);

  // ─── Render branches ────────────────────────────────────

  if (state.kind === 'idle' || state.kind === 'loading') {
    return (
      <AppShell>
        <div className="flex flex-col items-center gap-3 pt-8">
          <Skeleton className="h-12 w-40" />
          <Skeleton className="h-4 w-64" />
        </div>
        <div className="mt-4 flex flex-col gap-3">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
        <Skeleton className="mt-4 h-12 w-full" />
        <span className="sr-only">{t('common.loading')}</span>
      </AppShell>
    );
  }

  if (state.kind === 'unauthorized') {
    return (
      <AppShell>
        <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 text-center">
          <div
            className="flex h-16 w-16 items-center justify-center rounded-full bg-accent-purple/15 text-accent-purple"
            aria-hidden
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z" />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-text-primary">
            {t('onboarding.unauthorized.title')}
          </h1>
          <p className="max-w-sm text-sm text-text-secondary">
            {t('onboarding.unauthorized.body')}
          </p>
        </div>
      </AppShell>
    );
  }

  if (state.kind === 'error') {
    return (
      <AppShell>
        <Card className="mt-8 flex flex-col gap-4">
          <div>
            <h2 className="text-lg font-semibold text-text-primary">
              {t('common.error')}
            </h2>
            <p className="mt-1 text-sm text-text-secondary">{state.message}</p>
          </div>
          <Button onClick={handleRetry}>{t('common.tryAgain')}</Button>
        </Card>
      </AppShell>
    );
  }

  // state.kind === 'ready'
  // If welcomeName is set, the redirect timer is already running - show the
  // flash so the screen does not appear empty for those 300ms.
  if (welcomeName !== null) {
    return (
      <AppShell>
        <div className="flex min-h-[60vh] flex-col items-center justify-center gap-2 text-center">
          <span className="bg-gradient-to-r from-accent-gold to-accent-purple bg-clip-text text-3xl font-bold text-transparent">
            {t('onboarding.welcomeBack', { name: welcomeName })}
          </span>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      {/* Hero */}
      <div className="flex flex-col items-center gap-3 pt-6 text-center">
        <h1 className="bg-gradient-to-r from-accent-gold to-accent-purple bg-clip-text text-[40px] font-bold leading-tight text-transparent">
          {t('onboarding.hero.title')}
        </h1>
        <p className="max-w-sm text-base text-text-secondary">
          {t('onboarding.hero.tagline')}
        </p>
      </div>

      {/* Feature bullets */}
      <div className="mt-2 flex flex-col gap-3">
        {FEATURES.map((feature) => {
          const Icon = feature.icon;
          return (
            <Card
              key={feature.titleKey}
              className="flex items-start gap-3 border-border/60"
            >
              <div
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-accent-purple/15 text-accent-purple"
                aria-hidden
              >
                <Icon className="h-5 w-5" />
              </div>
              <div className="flex flex-col gap-1">
                <p className="text-sm font-semibold text-text-primary">
                  {t(feature.titleKey)}
                </p>
                <p className="text-xs leading-relaxed text-text-secondary">
                  {t(feature.bodyKey)}
                </p>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Wallet-link error banner */}
      {linkError !== null && (
        <Card className="border-danger/60 bg-danger/10">
          <p className="text-sm text-text-primary">{linkError}</p>
          <Button
            variant="outline"
            size="sm"
            className="mt-3"
            onClick={() => {
              setLinkError(null);
              previousAddress.current = '';
            }}
          >
            {t('common.tryAgain')}
          </Button>
        </Card>
      )}

      {/* Primary CTA — direct Tonkeeper deep link (no wallet picker modal) */}
      <div className="mt-2 flex flex-col items-center gap-2">
        <Button
          size="lg"
          className="w-full bg-gradient-to-r from-accent-gold to-accent-purple font-semibold text-bg-primary shadow-lg shadow-accent-gold/20 hover:opacity-90"
          onClick={() => {
            haptics.medium();
            // Skip the wallet picker modal and jump straight to Tonkeeper.
            // openSingleWalletModal() triggers the Tonkeeper deep link on
            // mobile (or shows a minimal install-prompt modal if missing).
            void tonConnectUI.openSingleWalletModal('tonkeeper');
          }}
          disabled={linkingWallet}
        >
          <Wallet className="mr-2 h-5 w-5" aria-hidden />
          {linkingWallet
            ? t('common.loading')
            : t('onboarding.connectWallet')}
        </Button>
        <p className="text-[10px] text-text-muted">
          {t('onboarding.tonkeeperHint')}
        </p>
      </div>

      {/* Secondary skip */}
      <div className="mt-auto flex justify-center pt-4">
        <button
          type="button"
          onClick={handleSkip}
          className="text-xs font-medium text-text-muted underline-offset-4 hover:text-text-secondary hover:underline"
        >
          {t('onboarding.skipForNow')} →
        </button>
      </div>
    </AppShell>
  );
}
